/* eslint-disable no-unused-vars */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  User,
} from 'firebase/auth'
import { auth, googleProvider } from '@/lib/firebase'
import {
  checkUserSubscription,
  isSubscriptionActive,
  UserSubscription,
} from '@/services/subscription'
import { validatePromoCode, decrementPromoCodeUses } from '@/services/promoCode'

interface AuthContextType {
  user: User | null
  loading: boolean
  subscription: UserSubscription | null
  hasActiveSubscription: boolean | null
  subscriptionLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  googleSignIn: (promoCode?: string) => Promise<void>
  logout: () => Promise<void>
  refreshSubscription: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext)!

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [subscription, setSubscription] = useState<UserSubscription | null>(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)

  const refreshSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null)
      setHasActiveSubscription(false)
      setSubscriptionLoading(false)
      return
    }

    setSubscriptionLoading(true)
    try {
      const userSubscription = await checkUserSubscription(user.uid)
      setSubscription(userSubscription)
      setHasActiveSubscription(isSubscriptionActive(userSubscription))
    } catch (error) {
      console.error('Error refreshing subscription:', error)
      setSubscription(null)
      setHasActiveSubscription(false)
    } finally {
      setSubscriptionLoading(false)
    }
  }, [user])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u)

      if (u) {
        setSubscriptionLoading(true)
        checkUserSubscription(u.uid)
          .then((userSubscription) => {
            setSubscription(userSubscription)
            setHasActiveSubscription(isSubscriptionActive(userSubscription))
          })
          .catch((err) => {
            console.error('Error refreshing subscription', err)
            setSubscription(null)
            setHasActiveSubscription(false)
          })
          .finally(() => {
            setSubscriptionLoading(false)
            setLoading(false)
          })
      } else {
        setSubscription(null)
        setHasActiveSubscription(false)
        setSubscriptionLoading(false)
        setLoading(false)
      }
    })

    return unsubscribe
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password)
  }, [])

  const register = useCallback(async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password)
  }, [])

  const googleSignIn = useCallback(async (promoCode?: string) => {
    // Perform Google sign-in first
    const result = await signInWithPopup(auth, googleProvider)

    // Check if user is new by comparing creation time with current time
    const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime

    // For new users, require a valid promo code
    if (isNewUser) {
      if (!promoCode) {
        await signOut(auth)
        // Redirect to home with error parameter
        window.location.href = '/?registerError=promoMissing'
        return
      }

      const validation = await validatePromoCode(promoCode)
      if (!validation.isValid) {
        await signOut(auth)
        // Redirect to home with error parameter
        const errorParam = encodeURIComponent(validation.error || 'Promo code is invalid')
        window.location.href = `/?registerError=promoInvalid&message=${errorParam}`
        return
      }

      // Use the promo code for new user
      try {
        await decrementPromoCodeUses(promoCode)
      } catch (error) {
        console.error('Failed to use promo code:', error)
        // If we can't use the promo code, delete the account and redirect with error
        await signOut(auth)
        window.location.href = '/?registerError=promoUsageFailed'
        return
      }
    }
    // Existing users can sign in normally without promo code validation
  }, [])

  const logout = useCallback(async () => {
    await signOut(auth)
    // Clear all persisted state from sessionStorage
    sessionStorage.clear()
    // Force a page reload to ensure clean state
    window.location.reload()
  }, [])

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      subscription,
      hasActiveSubscription,
      subscriptionLoading,
      login,
      register,
      googleSignIn,
      logout,
      refreshSubscription,
    }),
    [
      user,
      loading,
      subscription,
      hasActiveSubscription,
      subscriptionLoading,
      login,
      register,
      googleSignIn,
      logout,
      refreshSubscription,
    ]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
